import { useMemo } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiPlaceholderArea } from "@miplatform/components";
import { SWAP_DIRECTION } from "@miplatform/constants";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IWorkOrderTemplateData } from "../ConfigWorkOrderTemplates.types";
import { ConfigWorkOrderStepCard } from "../components/ConfigWorkOrderStepCard";

export const WorkOrderStepsTabView = ({
  form,
  extraTabProps: { isReadOnly } = {},
}: WorkOrderStepsTabViewProps) => {
  const { classes, theme } = useStyles();
  const { setValue, watch } = form;

  const steps = watch("steps") ?? [];
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.priorityOrder - b.priorityOrder),
    [steps]
  );

  const handleMoveStep = (
    index: number,
    direction: (typeof SWAP_DIRECTION)[keyof typeof SWAP_DIRECTION]
  ): void => {
    const newSteps = [...sortedSteps];
    const [movedItem] = newSteps.splice(index, 1);

    const newIndex = direction === SWAP_DIRECTION.UP ? index - 1 : index + 1;
    newSteps.splice(newIndex, 0, movedItem);

    const updatedSteps = newSteps.map((step, i) => ({
      ...step,
      priorityOrder: i + 1,
    }));

    setValue("steps", updatedSteps, { shouldDirty: true });
  };

  return (
    <ConfigAccordionHeader title="Work Order Step(s)">
      {!sortedSteps || sortedSteps.length === 0 ? (
        <MiPlaceholderArea
          text="No Steps Created"
          helperText="Tip: Create and link Steps to see them here."
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            flex: 1,
            minHeight: 500,
          }}
        />
      ) : (
        <Flipper
          flipKey={sortedSteps.map((step) => step.id).join("-")}
          className={classes.list}
        >
          {sortedSteps.map((step, index) => (
            <Flipped key={step.id} flipId={step.id}>
              {(flippedProps) => (
                <div {...flippedProps}>
                  <ConfigWorkOrderStepCard
                    step={step}
                    index={index}
                    disableUp={isReadOnly || index === 0}
                    disableDown={isReadOnly || index === sortedSteps.length - 1}
                    onReorderUp={() => handleMoveStep(index, SWAP_DIRECTION.UP)}
                    onReorderDown={() =>
                      handleMoveStep(index, SWAP_DIRECTION.DOWN)
                    }
                  />
                </div>
              )}
            </Flipped>
          ))}
        </Flipper>
      )}
    </ConfigAccordionHeader>
  );
};

interface WorkOrderStepsTabViewProps {
  form: UseFormReturn<IWorkOrderTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));
